import React, { useEffect, useMemo, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import spformsApi from '../lib/spformsApi';
import { useTenant } from '../contexts/TenantContext';
import { formatDateTimeEU } from '../utils/dateFormat';

const { FiPlus, FiRefreshCw, FiEdit2, FiX, FiSave } = FiIcons;

const TENANT_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
];

const EMAIL_DELIVERY_MODES = [
  { value: 'default_only', label: 'Default only (Sportiko/Resend)' },
  { value: 'custom_only', label: 'Custom only (tenant SMTP)' },
  { value: 'both', label: 'Both' },
  { value: 'disabled', label: 'Disabled' },
];

function emptyCreateForm() {
  return {
    name: '',
    slug: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    notes: '',
    admin_name: '',
    admin_email: '',
    admin_password: '',
  };
}

function emptyEditForm() {
  return {
    id: null,
    name: '',
    slug: '',
    status: 'active',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    notes: '',
    smtp_enabled: false,
    smtp_duplicate_confirmation_enabled: false,
    smtp_host: '',
    smtp_port: 465,
    smtp_secure: true,
    smtp_username: '',
    smtp_password: '',
    smtp_from_name: '',
    smtp_from_email: '',
    smtp_reply_to_email: '',
    email_delivery_approval: 'default_only',
    email_delivery_payment: 'default_only',
    email_delivery_custom: 'default_only',
    email_delivery_bulk_custom: 'default_only',
  };
}

function slugify(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function badgeClasses(status) {
  switch (status) {
    case 'active':
      return 'bg-emerald-100 text-emerald-700';
    case 'inactive':
      return 'bg-amber-100 text-amber-700';
    case 'suspended':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

export default function Tenants() {
  const { isSuperadmin } = useTenant();

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const [createForm, setCreateForm] = useState(emptyCreateForm());
  const [editForm, setEditForm] = useState(emptyEditForm());

  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const loadTenants = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      const data = await spformsApi.adminTenantsList();
      setTenants(data);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperadmin) {
      loadTenants();
    } else {
      setLoading(false);
    }
  }, [isSuperadmin]);

  const filteredTenants = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter((tenant) => {
      return (
        String(tenant.name || '').toLowerCase().includes(q) ||
        String(tenant.slug || '').toLowerCase().includes(q) ||
        String(tenant.contact_name || '').toLowerCase().includes(q) ||
        String(tenant.contact_email || '').toLowerCase().includes(q)
      );
    });
  }, [tenants, search]);

  const openCreateModal = () => {
    setCreateForm(emptyCreateForm());
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setCreateForm(emptyCreateForm());
    setShowCreateModal(false);
  };

  const openEditModal = (tenant) => {
    setEditForm({
      id: tenant.id,
      name: tenant.name || '',
      slug: tenant.slug || '',
      status: tenant.status || 'active',
      contact_name: tenant.contact_name || '',
      contact_email: tenant.contact_email || '',
      contact_phone: tenant.contact_phone || '',
      notes: tenant.notes || '',
      smtp_enabled: Boolean(tenant.smtp_enabled),
      smtp_duplicate_confirmation_enabled: Boolean(
        tenant.smtp_duplicate_confirmation_enabled
      ),
      smtp_host: tenant.smtp_host || '',
      smtp_port: tenant.smtp_port || 465,
      smtp_secure: tenant.smtp_secure !== false,
      smtp_username: tenant.smtp_username || '',
      smtp_password: '',
      smtp_from_name: tenant.smtp_from_name || '',
      smtp_from_email: tenant.smtp_from_email || '',
      smtp_reply_to_email: tenant.smtp_reply_to_email || '',
      email_delivery_approval: tenant.email_delivery_approval || 'default_only',
      email_delivery_payment: tenant.email_delivery_payment || 'default_only',
      email_delivery_custom: tenant.email_delivery_custom || 'default_only',
      email_delivery_bulk_custom:
        tenant.email_delivery_bulk_custom || 'default_only',
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setEditForm(emptyEditForm());
    setShowEditModal(false);
  };

  const handleCreateChange = (field, value) => {
    setCreateForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'name') {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setErrorMessage('');
      setSuccessMessage('');
      await spformsApi.adminTenantCreateWithAdmin({
        name: createForm.name,
        slug: createForm.slug,
        contact_name: createForm.contact_name,
        contact_email: createForm.contact_email,
        contact_phone: createForm.contact_phone,
        notes: createForm.notes,
        admin_name: createForm.admin_name,
        admin_email: createForm.admin_email,
        admin_password: createForm.admin_password,
      });
      setSuccessMessage('Tenant and first admin created successfully');
      closeCreateModal();
      await loadTenants();
    } catch (error) {
      setErrorMessage(error.message || 'Failed to create tenant with admin');
    } finally {
      setSaving(false);
    }
  };

  const handleEditTenant = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setErrorMessage('');
      setSuccessMessage('');
      await spformsApi.adminTenantUpdate({
        id: editForm.id,
        name: editForm.name,
        slug: editForm.slug,
        status: editForm.status,
        contact_name: editForm.contact_name,
        contact_email: editForm.contact_email,
        contact_phone: editForm.contact_phone,
        notes: editForm.notes,
        smtp_enabled: editForm.smtp_enabled,
        smtp_duplicate_confirmation_enabled:
          editForm.smtp_duplicate_confirmation_enabled,
        smtp_host: editForm.smtp_host,
        smtp_port: editForm.smtp_port,
        smtp_secure: editForm.smtp_secure,
        smtp_username: editForm.smtp_username,
        smtp_password: editForm.smtp_password,
        smtp_from_name: editForm.smtp_from_name,
        smtp_from_email: editForm.smtp_from_email,
        smtp_reply_to_email: editForm.smtp_reply_to_email,
        email_delivery_approval: editForm.email_delivery_approval,
        email_delivery_payment: editForm.email_delivery_payment,
        email_delivery_custom: editForm.email_delivery_custom,
        email_delivery_bulk_custom: editForm.email_delivery_bulk_custom,
      });
      setSuccessMessage('Tenant updated successfully');
      closeEditModal();
      await loadTenants();
    } catch (error) {
      setErrorMessage(error.message || 'Failed to update tenant');
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperadmin) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
        Tenant management is available only to superadmin users.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tenants</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage SportikoForms SaaS tenants and onboard clients with their first admin.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={loadTenants}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <SafeIcon icon={FiRefreshCw} /> Refresh
          </button>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
          >
            <SafeIcon icon={FiPlus} /> Create Tenant + Admin
          </button>
        </div>
      </div>

      {errorMessage ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {errorMessage}
        </div>
      ) : null}

      {successMessage ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {successMessage}
        </div>
      ) : null}

      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by tenant, slug, contact..."
            className="w-full md:max-w-md rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <div className="text-sm text-gray-500">
            {filteredTenants.length} tenant
            {filteredTenants.length === 1 ? '' : 's'}
          </div>
        </div>

        <div className="mt-4 overflow-x-auto">
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-500">
              Loading tenants...
            </div>
          ) : filteredTenants.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500">
              No tenants found.
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-3">Tenant</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredTenants.map((tenant) => (
                  <tr key={tenant.id} className="align-top">
                    <td className="px-4 py-4">
                      <div className="font-medium text-gray-900">
                        {tenant.name}
                      </div>
                      <div className="mt-1 text-xs text-gray-500 break-all">
                        {tenant.slug}
                      </div>
                      <div className="mt-1 text-xs text-gray-400 break-all">
                        {tenant.id}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      <div>{tenant.contact_name || '-'}</div>
                      <div className="text-xs text-gray-500">
                        {tenant.contact_email || '-'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {tenant.contact_phone || '-'}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${badgeClasses(
                          tenant.status
                        )}`}
                      >
                        {tenant.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      {formatDateTimeEU(tenant.created_at)}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex justify-end">
                        <button
                          onClick={() => openEditModal(tenant)}
                          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          <SafeIcon icon={FiEdit2} /> Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showCreateModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Create Tenant + First Admin
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Create a new tenant and immediately provision its first tenant admin.
                </p>
              </div>
              <button
                onClick={closeCreateModal}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
              >
                <SafeIcon icon={FiX} />
              </button>
            </div>

            <form onSubmit={handleCreateTenant} className="space-y-6 px-6 py-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3">
                  Tenant Details
                </h3>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Name
                    </label>
                    <input
                      type="text"
                      value={createForm.name}
                      onChange={(e) =>
                        handleCreateChange('name', e.target.value)
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Slug
                    </label>
                    <input
                      type="text"
                      value={createForm.slug}
                      onChange={(e) =>
                        handleCreateChange('slug', e.target.value)
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Contact Name
                    </label>
                    <input
                      type="text"
                      value={createForm.contact_name}
                      onChange={(e) =>
                        handleCreateChange('contact_name', e.target.value)
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Contact Email
                    </label>
                    <input
                      type="email"
                      value={createForm.contact_email}
                      onChange={(e) =>
                        handleCreateChange('contact_email', e.target.value)
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Contact Phone
                    </label>
                    <input
                      type="text"
                      value={createForm.contact_phone}
                      onChange={(e) =>
                        handleCreateChange('contact_phone', e.target.value)
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Notes
                    </label>
                    <textarea
                      rows={3}
                      value={createForm.notes}
                      onChange={(e) =>
                        handleCreateChange('notes', e.target.value)
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3">
                  First Admin
                </h3>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Admin Name
                    </label>
                    <input
                      type="text"
                      value={createForm.admin_name}
                      onChange={(e) =>
                        handleCreateChange('admin_name', e.target.value)
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Admin Email
                    </label>
                    <input
                      type="email"
                      value={createForm.admin_email}
                      onChange={(e) =>
                        handleCreateChange('admin_email', e.target.value)
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Admin Password
                    </label>
                    <input
                      type="password"
                      value={createForm.admin_password}
                      onChange={(e) =>
                        handleCreateChange('admin_password', e.target.value)
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <SafeIcon icon={FiX} /> Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white ${
                    saving
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  }`}
                >
                  <SafeIcon icon={FiSave} />{' '}
                  {saving ? 'Saving...' : 'Create Tenant + Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {showEditModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Edit Tenant
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Update tenant details, SMTP sender, and email delivery rules.
                </p>
              </div>
              <button
                onClick={closeEditModal}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
              >
                <SafeIcon icon={FiX} />
              </button>
            </div>

            <form onSubmit={handleEditTenant} className="space-y-5 px-6 py-6">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Name
                  </label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => handleEditChange('name', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Slug
                  </label>
                  <input
                    type="text"
                    value={editForm.slug}
                    onChange={(e) => handleEditChange('slug', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <select
                    value={editForm.status}
                    onChange={(e) => handleEditChange('status', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {TENANT_STATUSES.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Contact Name
                  </label>
                  <input
                    type="text"
                    value={editForm.contact_name}
                    onChange={(e) =>
                      handleEditChange('contact_name', e.target.value)
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    value={editForm.contact_email}
                    onChange={(e) =>
                      handleEditChange('contact_email', e.target.value)
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Contact Phone
                  </label>
                  <input
                    type="text"
                    value={editForm.contact_phone}
                    onChange={(e) =>
                      handleEditChange('contact_phone', e.target.value)
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Notes
                  </label>
                  <textarea
                    rows={4}
                    value={editForm.notes}
                    onChange={(e) => handleEditChange('notes', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="rounded-2xl border border-indigo-100 bg-indigo-50/50 p-5">
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Tenant SMTP Email
                  </h3>
                  <p className="mt-1 text-xs text-gray-600">
                    Optional tenant mailbox sender. Registration confirmation
                    still always uses Sportiko Forms / Resend and SendFox sync.
                    If duplicate confirmation is enabled, the same confirmation
                    can also be sent from the tenant mailbox.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <label className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 text-sm">
                    <input
                      type="checkbox"
                      checked={Boolean(editForm.smtp_enabled)}
                      onChange={(e) =>
                        handleEditChange('smtp_enabled', e.target.checked)
                      }
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span>
                      <span className="block font-medium text-gray-800">
                        Enable tenant SMTP
                      </span>
                      <span className="block text-xs text-gray-500">
                        Allow this tenant mailbox to be used as a custom sender.
                      </span>
                    </span>
                  </label>

                  <label className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 text-sm">
                    <input
                      type="checkbox"
                      checked={Boolean(
                        editForm.smtp_duplicate_confirmation_enabled
                      )}
                      onChange={(e) =>
                        handleEditChange(
                          'smtp_duplicate_confirmation_enabled',
                          e.target.checked
                        )
                      }
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span>
                      <span className="block font-medium text-gray-800">
                        Send duplicate registration confirmation
                      </span>
                      <span className="block text-xs text-gray-500">
                        Registration confirmation remains Resend + SendFox.
                        This adds a second copy from the tenant mailbox.
                      </span>
                    </span>
                  </label>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      SMTP Host
                    </label>
                    <input
                      type="text"
                      value={editForm.smtp_host}
                      onChange={(e) =>
                        handleEditChange('smtp_host', e.target.value)
                      }
                      placeholder="smtp.stackmail.com"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      SMTP Port
                    </label>
                    <input
                      type="number"
                      value={editForm.smtp_port}
                      onChange={(e) =>
                        handleEditChange('smtp_port', e.target.value)
                      }
                      placeholder="465"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <label className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 text-sm md:col-span-2">
                    <input
                      type="checkbox"
                      checked={editForm.smtp_secure !== false}
                      onChange={(e) =>
                        handleEditChange('smtp_secure', e.target.checked)
                      }
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span>
                      <span className="block font-medium text-gray-800">
                        Use SSL/TLS secure SMTP
                      </span>
                      <span className="block text-xs text-gray-500">
                        Use ON for port 465. Use OFF only for non-SSL ports like
                        587/25 if required.
                      </span>
                    </span>
                  </label>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      SMTP Username
                    </label>
                    <input
                      type="text"
                      value={editForm.smtp_username}
                      onChange={(e) =>
                        handleEditChange('smtp_username', e.target.value)
                      }
                      placeholder="info@elitesoccerskills.gr"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      SMTP Password
                    </label>
                    <input
                      type="password"
                      value={editForm.smtp_password}
                      onChange={(e) =>
                        handleEditChange('smtp_password', e.target.value)
                      }
                      placeholder="Leave blank to keep existing password"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      From Name
                    </label>
                    <input
                      type="text"
                      value={editForm.smtp_from_name}
                      onChange={(e) =>
                        handleEditChange('smtp_from_name', e.target.value)
                      }
                      placeholder="Elite Soccer Skills"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      From Email
                    </label>
                    <input
                      type="email"
                      value={editForm.smtp_from_email}
                      onChange={(e) =>
                        handleEditChange('smtp_from_email', e.target.value)
                      }
                      placeholder="info@elitesoccerskills.gr"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Reply-To Email
                    </label>
                    <input
                      type="email"
                      value={editForm.smtp_reply_to_email}
                      onChange={(e) =>
                        handleEditChange('smtp_reply_to_email', e.target.value)
                      }
                      placeholder="info@elitesoccerskills.gr"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-emerald-100 bg-emerald-50/50 p-5">
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Email Delivery Rules
                  </h3>
                  <p className="mt-1 text-xs text-gray-600">
                    Registration confirmation is locked: it always sends through
                    Sportiko Forms / Resend and syncs to SendFox for marketing
                    collection. If tenant SMTP duplicate confirmation is enabled
                    above, the tenant mailbox also sends a duplicate
                    confirmation. The rules below control all other email types.
                  </p>
                </div>

                <div className="rounded-xl border border-emerald-200 bg-white p-4 text-xs text-gray-600 mb-5">
                  <div className="font-semibold text-gray-800 mb-2">
                    Delivery mode meaning
                  </div>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    <div>
                      <strong>Default only:</strong> Sportiko Forms / Resend
                      only.
                    </div>
                    <div>
                      <strong>Custom only:</strong> tenant SMTP mailbox only.
                    </div>
                    <div>
                      <strong>Both:</strong> Sportiko Forms / Resend + tenant
                      SMTP.
                    </div>
                    <div>
                      <strong>Disabled:</strong> do not send this email type.
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Approval emails
                    </label>
                    <select
                      value={editForm.email_delivery_approval}
                      onChange={(e) =>
                        handleEditChange(
                          'email_delivery_approval',
                          e.target.value
                        )
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {EMAIL_DELIVERY_MODES.map((mode) => (
                        <option key={mode.value} value={mode.value}>
                          {mode.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Payment emails
                    </label>
                    <select
                      value={editForm.email_delivery_payment}
                      onChange={(e) =>
                        handleEditChange(
                          'email_delivery_payment',
                          e.target.value
                        )
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {EMAIL_DELIVERY_MODES.map((mode) => (
                        <option key={mode.value} value={mode.value}>
                          {mode.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Custom single emails
                    </label>
                    <select
                      value={editForm.email_delivery_custom}
                      onChange={(e) =>
                        handleEditChange(
                          'email_delivery_custom',
                          e.target.value
                        )
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {EMAIL_DELIVERY_MODES.map((mode) => (
                        <option key={mode.value} value={mode.value}>
                          {mode.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Custom bulk emails
                    </label>
                    <select
                      value={editForm.email_delivery_bulk_custom}
                      onChange={(e) =>
                        handleEditChange(
                          'email_delivery_bulk_custom',
                          e.target.value
                        )
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {EMAIL_DELIVERY_MODES.map((mode) => (
                        <option key={mode.value} value={mode.value}>
                          {mode.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <SafeIcon icon={FiX} /> Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white ${
                    saving
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  }`}
                >
                  <SafeIcon icon={FiSave} />{' '}
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}